import React, { useState, useCallback } from 'react';
import { useLanguage } from '../../../language/hooks/useLanguageHook';

const ROICalculatorSection = ({ onStartFreeTrial }) => {
  const { t } = useLanguage();

  // ROI calculator state
  const [roiInputs, setRoiInputs] = useState({
    monthlyReviews: '100', // Keep as strings for input control
    responseTime: '24',   // Keep as strings
    staffCost: '25'        // Keep as strings
  });

  // Memoized ROI calculation to prevent recalculation on every render
  const calculateROI = useCallback(() => {
    const monthlyReviewsNum = parseInt(roiInputs.monthlyReviews, 10);
    const responseTimeNum = parseInt(roiInputs.responseTime, 10);
    const staffCostNum = parseInt(roiInputs.staffCost, 10);

    // Basic validation
    if (isNaN(monthlyReviewsNum) || monthlyReviewsNum < 0 ||
        isNaN(responseTimeNum) || responseTimeNum <= 0 || // Response time must be > 0
        isNaN(staffCostNum) || staffCostNum < 0) {
      return {
        timeSaved: "0",
        costSaved: "0",
        responseImprovement: "0"
      };
    }

    // Calculate metrics
    const timePerReviewHours = responseTimeNum / 60; // Convert minutes to hours
    // Assuming FeedBacked saves 75% of the time
    const totalTimeSaved = Math.round(monthlyReviewsNum * timePerReviewHours * 0.75);
    const monthlyCostSaved = Math.round(totalTimeSaved * staffCostNum);
    // Assuming FeedBacked makes responses instant (or near instant - let's say 90% improvement for calculation)
    // const responseImprovement = Math.round((responseTimeNum - (responseTimeNum * 0.1)) / responseTimeNum * 100); // Example calculation: 90% improvement
    // Simpler: Calculate percentage reduction (e.g., 75% reduction means 75% improvement)
    const responseImprovement = 75; // Directly state the assumed improvement percentage


    return {
      timeSaved: totalTimeSaved.toString(),
      costSaved: monthlyCostSaved.toString(),
      // Ensure improvement doesn't exceed 100 or go below 0 if calculated differently
      responseImprovement: Math.min(100, Math.max(0, responseImprovement)).toString()
    };
  }, [roiInputs]); // Recalculate only when roiInputs change

  const roiResults = calculateROI();

  // Input handler with validation
  const handleInputChange = (field, value) => {
    // Allow only numbers (and potentially a single decimal for cost if needed, but integers seem intended)
    const numericValue = value.replace(/[^0-9]/g, '');

    // Prevent setting empty string which could cause NaN issues temporarily
    const finalValue = numericValue === '' ? '0' : numericValue;

    // Ensure non-negative values
    if (parseInt(finalValue, 10) < 0) {
        return; // Or set to '0' if preferred
    }

    setRoiInputs(prev => ({
      ...prev,
      [field]: finalValue
    }));
  };

  return (
    <div className="relative z-10 bg-gray-50 py-20 scroll-fade-in">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">
            {t("public.landing.roi.title")}
          </h2>
          <p className="text-xl text-gray-600">
            {t("public.landing.roi.subtitle")}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Input Section */}
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-xl font-bold mb-6">
              {t("public.landing.roi.metrics.title")}
            </h3>
            <div className="space-y-6">
              {/* Monthly Reviews Input */}
              <div>
                <label htmlFor="monthlyReviews" className="block text-gray-700 mb-2">
                  {t("public.landing.roi.metrics.monthlyReviews.label")}
                </label>
                <div className="relative">
                  <input
                    id="monthlyReviews"
                    type="text" // Use text for better control with regex
                    inputMode="numeric" // Hint for mobile keyboards
                    pattern="[0-9]*" // Pattern for validation
                    value={roiInputs.monthlyReviews}
                    onChange={(e) => handleInputChange('monthlyReviews', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:border-blue-500 pr-20 transition-colors duration-200 ease-in-out" // Added focus offset & transition
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
                    {t("public.landing.roi.metrics.monthlyReviews.suffix")}
                  </span>
                </div>
              </div>

              {/* Response Time Input */}
              <div>
                <label htmlFor="responseTime" className="block text-gray-700 mb-2">
                  {t("public.landing.roi.metrics.responseTime.label")}
                </label>
                <div className="relative">
                  <input
                    id="responseTime"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={roiInputs.responseTime}
                    onChange={(e) => handleInputChange('responseTime', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:border-blue-500 pr-20 transition-colors duration-200 ease-in-out" // Added focus offset & transition
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
                    {t("public.landing.roi.metrics.responseTime.suffix")}
                  </span>
                </div>
              </div>

              {/* Staff Cost Input */}
              <div>
                <label htmlFor="staffCost" className="block text-gray-700 mb-2">
                  {t("public.landing.roi.metrics.staffCost.label")}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
                    {t("public.landing.roi.metrics.staffCost.prefix")}
                  </span>
                  <input
                    id="staffCost"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={roiInputs.staffCost}
                    onChange={(e) => handleInputChange('staffCost', e.target.value)}
                    className="w-full pl-8 pr-16 py-2 border rounded-lg focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:border-blue-500 text-right transition-colors duration-200 ease-in-out" // Added focus offset & transition
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
                    {t("public.landing.roi.metrics.staffCost.suffix")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-xl font-bold mb-6">
              {t("public.landing.roi.results.title")}
            </h3>
            <div className="space-y-6">
              {/* Time Saved */}
              <div>
                <div className="text-gray-600 mb-1">
                  {t("public.landing.roi.results.timeSaved.label")}
                </div>
                <div className="text-3xl font-bold text-blue-600">
                  {roiResults.timeSaved} {t("public.landing.roi.results.timeSaved.suffix")}
                </div>
              </div>

              {/* Cost Saved */}
              <div>
                <div className="text-gray-600 mb-1">
                  {t("public.landing.roi.results.costSaved.label")}
                </div>
                <div className="text-3xl font-bold text-green-600">
                  {t("public.landing.roi.metrics.staffCost.prefix")}{roiResults.costSaved}
                </div>
              </div>

              {/* Response Improvement */}
              <div>
                <div className="text-gray-600 mb-1">
                  {t("public.landing.roi.results.responseImprovement.label")}
                </div>
                <div className="text-3xl font-bold text-purple-600">
                  {roiResults.responseImprovement}%
                </div>
              </div>

              <button
                onClick={onStartFreeTrial} // Use prop
                className="w-full bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-all duration-300 ease-in-out transform hover:scale-[1.03] hover:shadow-lg mt-8"
              >
                {t("public.landing.roi.results.cta")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROICalculatorSection; 